import { TFunction } from "i18next";
import { IBaseAsset } from "models/assets.models";
import {
  INotification,
  NotificationAssetType,
  NotificationCTAType,
} from "models/notificationItem.models";
import { NotificationTemplateType } from "utils/constants/notifications.constants";
import { Path } from "utils/constants/paths.constants";
import { getDocumentPath, getHAQPath } from "utils/helpers/routes";
import { getReadableAssetType } from "../NotificationsListItem.utils";

type TResolvedNotificationAsset = Pick<
  IBaseAsset,
  "id" | "parent" | "source" | "latestVersion"
> & {
  type: string;
};

// This will eventually be expanded when other notification types are supported
export const getReadableCTAType = (
  notification: INotification,
  t: TFunction,
): string => {
  const { metadata, templateType } = notification;
  const { ctaType, assetType } = metadata;

  if (ctaType === NotificationCTAType.Project) {
    return t("navigation.notifications.notificationCTATypes.project");
  }

  if (ctaType === NotificationCTAType.Task) {
    return t("navigation.notifications.notificationCTATypes.task");
  }

  if (ctaType === NotificationCTAType.Milestones) {
    return t("navigation.notifications.notificationCTATypes.milestones");
  }

  if (ctaType === NotificationCTAType.Asset && assetType) {
    if (templateType === NotificationTemplateType.DocumentCopy) {
      return t("navigation.notifications.notificationCTATypes.document");
    }

    return getReadableAssetType(assetType, t);
  }

  if (ctaType === NotificationCTAType.HAQs) {
    return t("navigation.notifications.notificationCTATypes.questions");
  }

  if (ctaType === NotificationCTAType.Content) {
    const { numOfDocuments, documentNames } = metadata;

    return documentNames && numOfDocuments && Number(numOfDocuments) <= 1
      ? documentNames.slice(1, -1).split(",")[0]
      : t("navigation.notifications.notificationCTATypes.content");
  }

  return "";
};

export const getCTALink = (
  notification: INotification,
  resolvedAsset?: TResolvedNotificationAsset,
): string => {
  const { metadata, templateType } = notification;

  const { ctaType, assetType, assetId, projectId, parentAssetId, groupId } =
    metadata;

  if (ctaType === NotificationCTAType.Project && projectId) {
    return Path.Project.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Task && groupId) {
    return Path.Task.replace(":taskGroupId", groupId ?? "");
  }

  if (ctaType === NotificationCTAType.Milestones && projectId) {
    return Path.ProjectMilestones.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.HAQs) {
    return Path.ProjectHAQs.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Content) {
    return Path.ProjectDocuments.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Asset) {
    // This will eventually be expanded with other values
    switch (assetType) {
      case NotificationAssetType.Question:
        return getHAQPath(projectId, resolvedAsset?.id ?? assetId);

      case NotificationAssetType.QuestionResponse:
        return parentAssetId ? getHAQPath(projectId, parentAssetId) : "";

      case NotificationAssetType.ImportedHAQ:
        return templateType !==
          NotificationTemplateType.AssetHAQImportParsingFail
          ? Path.ProjectHAQs.replace(":projectId", projectId ?? "")
          : "";

      default:
        return getDocumentPath(
          projectId,
          resolvedAsset ??
            (assetId && assetType
              ? {
                  id: assetId,
                  type: assetType,
                }
              : undefined),
        );
    }
  }

  return "";
};

export const shouldResolveAssetForCTA = (
  notification: INotification,
): boolean => {
  const {
    metadata: { ctaType, assetSourceArn, assetType },
  } = notification;

  if (ctaType !== NotificationCTAType.Asset) {
    return false;
  }

  if (assetSourceArn) {
    return true;
  }

  return assetType === NotificationAssetType.StructuredDocumentChild;
};
